import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHead, Panel, SButton } from "@/components/staff/Bits";

export default function Survey() {
  const { can } = useAuth();
  return can("survey:manage") ? <SurveyManager /> : <Navigate to="/office" replace />;
}

function SurveyManager() {
  const [status, setStatus] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/survey/status").then((r) => setStatus(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const upload = async () => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { const { data } = await api.post("/survey/import", fd); toast.success(`${data.count} survey records loaded`); setFile(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm("Remove all Parish Survey records from MABUHAY CARE? Cases already verified keep their verification.")) return;
    try { await api.delete("/survey"); toast.success("Survey records removed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-3xl space-y-5">
      <PageHead title="Parish Survey link" sub="Load an export of the Parish Survey so staff can confirm that a requester is a parishioner and fill in missing details with one tap. Survey records are used only for this check. They never create cases and are never shown on the public site." />
      <Panel title="Current survey records" testid="survey-status">
        {!status ? <p>Loading…</p> : status.count ? (
          <p className="text-[15px]"><b data-testid="survey-count">{status.count}</b> records from <b>{status.filename}</b>, loaded {fmtDateTime(status.imported_at)} by {status.imported_by}. Columns found: {status.columns?.join(", ")}.</p>
        ) : <p data-testid="survey-empty" className="text-[15px] text-[#605349]">No survey records loaded yet.</p>}
        {status?.count > 0 && <SButton className="mt-4" variant="ghost" data-testid="survey-clear-button" onClick={clear}>Remove survey records</SButton>}
      </Panel>
      <Panel title="Load or replace survey export" testid="survey-upload">
        <p className="text-[15px] mb-3">Upload an Excel (.xlsx) or CSV file whose first row contains column names such as <b>Survey reference</b>, <b>Full name</b>, <b>GKK</b>, <b>Address</b> and <b>Contact number</b>. Only <b>Full name</b> is required. Include only these identity columns; do not include survey answers. A new upload replaces the previous list.</p>
        <input type="file" accept=".xlsx,.csv" data-testid="survey-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block text-sm" />
        <SButton className="mt-4" data-testid="survey-upload-button" disabled={!file || busy} onClick={upload}><Upload className="w-4 h-4" />{busy ? "Loading…" : "Load survey records"}</SButton>
      </Panel>
      <p className="text-sm text-[#605349]">If the Parish Survey system later provides a secure online connection (API), it can replace this upload without changing how staff work.</p>
    </div>
  );
}
